import { useMemo } from 'react';

import { Metrics } from 'components/insights/constants';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import { NetworkPerformanceMetricDrillKey } from 'stores/networkPerformanceStore';

import { findRollupByGroupingValue, groupingValueFor, totalDimsFor } from '../../shared/slideout/rollupHelpers';
import useNetworkPerformanceAnalytics from '../query/useNetworkPerformanceAnalytics';

import { getQueryParams, METRIC_CONFIGS } from './constants';

export type NetworkPerformanceEntityRow = {
  name: string;
  id: string;
  value: number;
};

export type NetworkPerformanceSlideoutAnalyticsResult = {
  loading: boolean;
  rollups: AnalyticsResponseRollupCollection;
  overallValue: number | null;
  highestEntity: NetworkPerformanceEntityRow | null;
  lowestEntity: NetworkPerformanceEntityRow | null;
  entityRows: NetworkPerformanceEntityRow[];
  counterCount: number;
  counterLoading: boolean;
};

function extractEntityRows(
  rollups: AnalyticsResponseRollupCollection,
  primaryMetric: Metrics
): NetworkPerformanceEntityRow[] {
  const totalDims = totalDimsFor(rollups);
  const entityTarget = groupingValueFor(totalDims, new Set([0, 1]));

  // When entityTarget is 0 (2-dim queries like Engagements with no sub-dimensions),
  // the entity rows ARE grouping 0, which AnalyticsResponseRollupCollection filters out.
  // Read directly from responseData in that case.
  if (entityTarget === 0) {
    const { analyticsResponse } = rollups;
    const metricIdx = analyticsResponse.findMetricIndex(primaryMetric);
    return analyticsResponse.responseData
      .filter((row) => (row.grouping ?? 0) === 0 && (row.datesIndex ?? 0) === 0)
      .map((row) => ({
        name: String(row.dimensionValues[0]?.value ?? ''),
        id: String(row.dimensionValues[1]?.value ?? ''),
        value: metricIdx >= 0 ? Number(row.metricValues[metricIdx]?.value ?? 0) : 0,
      }))
      .filter((r) => r.name);
  }

  const entityRollup = findRollupByGroupingValue(rollups, entityTarget);
  if (!entityRollup) return [];

  const names = entityRollup.dimensions.getCurrentDimensionValuesByIndex(0);
  const ids = entityRollup.dimensions.getCurrentDimensionValuesByIndex(1);
  const values = entityRollup.metrics.getCurrentSeries(primaryMetric);

  return names
    .map((name, i) => ({
      name,
      id: ids[i] ?? '',
      value: values[i] ?? 0,
    }))
    .filter((r) => r.name);
}

export default function useNetworkPerformanceSlideoutAnalytics(
  metric: NetworkPerformanceMetricDrillKey
): NetworkPerformanceSlideoutAnalyticsResult {
  const metricConfig = METRIC_CONFIGS[metric];
  const queryParams = getQueryParams(metric);

  const { loading, rollups } = useNetworkPerformanceAnalytics(queryParams.params, {
    queryKey: `networkPerformanceSlideout:${metric}`,
  });

  const { data: counterData, loading: counterLoading } = useNetworkPerformanceAnalytics(queryParams.counterParams, {
    queryKey: `networkPerformanceSlideout:counter:${metric}`,
  });

  const counterCount = useMemo(
    () => counterData.dimensions.getCurrentDimensionValuesByIndex(0).filter(Boolean).length,
    [counterData]
  );

  return useMemo(() => {
    const { primaryMetric } = metricConfig;
    const totalDims = totalDimsFor(rollups);
    const grandTotalTarget = groupingValueFor(totalDims, new Set());
    const grandTotal = findRollupByGroupingValue(rollups, grandTotalTarget);
    const overallValue = grandTotal?.metrics.getCurrentSeries(primaryMetric)[0] ?? null;
    const entityRows = extractEntityRows(rollups, primaryMetric);

    let highestEntity: NetworkPerformanceEntityRow | null = null;
    let lowestEntity: NetworkPerformanceEntityRow | null = null;

    if (entityRows.length > 0) {
      highestEntity = entityRows[0];
      lowestEntity = entityRows[0];
      for (const row of entityRows) {
        if (row.value > highestEntity.value || (row.value === highestEntity.value && row.name < highestEntity.name)) {
          highestEntity = row;
        }
        if (row.value < lowestEntity.value || (row.value === lowestEntity.value && row.name < lowestEntity.name)) {
          lowestEntity = row;
        }
      }
    }

    return {
      loading,
      rollups,
      overallValue,
      highestEntity,
      lowestEntity,
      entityRows,
      counterCount,
      counterLoading,
    };
  }, [metricConfig, loading, rollups, counterCount, counterLoading]);
}
